import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Select, SelectOption } from '../../../../components/atoms/select/select';
import { Text } from '../../../../components/atoms/text/text';
import { useReportContext, ReportContext, Report } from '../../renderer/report-context';

const getDateString = (time: string) => {
  return new Date(time).toLocaleDateString(undefined, {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  });
};

interface CurrentReportCompletionLabelProps {
  readonly timestamp?: string;
  readonly status?: string;
}

const CurrentReportCompletionLabel = ({ status, timestamp }: CurrentReportCompletionLabelProps) => {
  const { t } = useTranslation();

  const labelKey =
    status === 'completed'
      ? 'report.blocks.reportSwitcher.currentReport'
      : 'report.blocks.reportSwitcher.partialReport';

  return (
    <Text>
      {status && t(labelKey)} {timestamp && getDateString(timestamp)}
    </Text>
  );
};

interface CurrentReportDisplayProps {
  readonly timestamp: string;
  readonly index: number;
  readonly total: number;
  readonly status: string;
}

const CurrentReportDisplay = ({ status, timestamp, index, total }: CurrentReportDisplayProps) => {
  const { t } = useTranslation();

  const isMostRecent = index === 0;

  return (
    <div className="ml-2 flex flex-row gap-1">
      <CurrentReportCompletionLabel status={status} timestamp={timestamp} />{' '}
      {t('report.blocks.reportSwitcher.reportInfo', {
        mostRecent: isMostRecent ? `${t('report.blocks.reportSwitcher.mostRecentLabel')} ` : '',
        index: index + 1,
        total: total,
      })}
    </div>
  );
};

const useNavigateOnSelect = (
  reports: ReportContext['reports'],
  navigateOnChange: ReportSwitcherProps['navigateOnChange']
) =>
  useCallback(
    (selectedId: string) => {
      const report = reports.find((r) => r.referralSessionId === selectedId);
      if (!report) throw new Error(`referral id for referral session id ${selectedId} not found`);
      navigateOnChange(`../report/${report?.referralId}/${selectedId}`);
    },
    [reports, navigateOnChange]
  );

interface UseOrderedReportsReturn {
  numberOfReports: number;
  reportsOrdered: ReportContext['reports'];
  selectedReport: ReportContext['reports'][0] | undefined;
  selectedReportIndex: number;
}
const dateToUse = (report: Report) => {
  return report.completedAt ?? report.updatedAt;
};

const useOrderedReports = (reports: Report[], currentId: string): UseOrderedReportsReturn =>
  useMemo(() => {
    const ordered = [...reports].sort((a, b) => dateToUse(b).localeCompare(dateToUse(a)));
    const index = ordered.findIndex((report) => report.referralSessionId === currentId);

    return {
      reportsOrdered: ordered,
      selectedReportIndex: index,
      selectedReport: index >= 0 ? ordered[index] : undefined,
      numberOfReports: reports.length,
    };
  }, [reports, currentId]);

interface ReportSwitcherProps {
  readonly navigateOnChange: (path: string) => void;
}

const ReportSwitcher = ({ navigateOnChange }: ReportSwitcherProps) => {
  const { reports, id } = useReportContext();
  const navigateOnSelect = useNavigateOnSelect(reports, navigateOnChange);

  const { numberOfReports, reportsOrdered, selectedReport, selectedReportIndex } = useOrderedReports(reports, id);

  return (
    <div className="flex gap-2 items-center">
      <div>
        {!selectedReport && 'no report'}
        {selectedReport && (
          <CurrentReportDisplay
            index={selectedReportIndex}
            status={selectedReport.status}
            timestamp={dateToUse(selectedReport)}
            total={numberOfReports}
          />
        )}
      </div>
      {numberOfReports > 1 && (
        <Select className="justify-self-end" onValueChange={navigateOnSelect} value={id}>
          {reportsOrdered.map((report) => (
            <SelectOption key={report.referralSessionId} value={report.referralSessionId}>{`${
              report.service
            } ${getDateString(dateToUse(report))}`}</SelectOption>
          ))}
        </Select>
      )}
    </div>
  );
};

export { CurrentReportCompletionLabel, CurrentReportDisplay, ReportSwitcher };
